import 'rsuite/dist/rsuite-no-reset.min.css'
import 'rsuite/DatePicker/styles/index.css'

import { zodResolver } from '@hookform/resolvers/zod'
import { useParams } from '@tanstack/react-router'
import { Button, message, Tooltip } from 'antd'
import { format } from 'date-fns'
import { useRolodexEdit } from 'features/fde'
import {
  getGroupingHierarchy,
  GroupingsSelectionModal,
  useGroupings,
} from 'features/groupings'
import { PermissionsGate } from 'features/permissions'
import {
  formValidationStyles,
  StyledFormInput,
  StyledFormSubmitButton,
} from 'features/rightDrawer/drawerStates/crudWorkflows/utils/utils'
import { useRightDrawerRolodexEntries } from 'features/rightDrawer/useRightDrawerRolodexEntries'
import { DrawerState } from 'features/rightDrawer/utils/enums'
import { RightSideDrawerProps } from 'features/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'features/rightDrawer/utils/RightDrawerProvider'
import { generateEntrySlug } from 'features/rolodex'
import { motion } from 'framer-motion'
import React, { BaseSyntheticEvent, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { CustomProvider, DatePicker } from 'rsuite'
import styled from 'styled-components'
import {
  GeckoSite,
  GeckoSiteGrouping,
  GeckoUnit,
  getSiteEditorPermission,
  PERMISSION_CANTILEVER_CORE_ADMIN,
  PERMISSION_CANTILEVER_CORE_EDIT,
  RolodexEntryJson,
  RolodexEntryPutJson,
} from 'types'
import { v4 as uuid4 } from 'uuid'

import { defaultValues, editUnitSchema } from './editUnitSchema'

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${formValidationStyles}
`
export const AddEditAssetButton = styled(motion.button)`
  all: unset;
  padding: 0.5rem 1rem;
  display: flex;
  color: ${({ theme }) => theme.forecast.text1};
  align-items: center;
  gap: 0.5rem;
  border-radius: 4px;
  background: ${({ theme }) => theme.forecast.background2} !important;
  font-family: Manrope;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 125%;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3} !important;
  }
`

const SelectedGroupingDisplay = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.background3};
  border-radius: 4px;
  font-size: 14px;
`

const GroupingName = styled.strong`
  color: ${({ theme }) => theme.forecast.text1};
`

const GroupingDescription = styled.div`
  color: ${({ theme }) => theme.forecast.text3};
  margin-top: 4px;
  font-size: 13px;
`

const StyledHeaderDiv = styled.div`
  padding-bottom: 15px;
`

const StyledFormRoot = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const EditGeckoUnitProps: RightSideDrawerProps = {
  title: 'Edit Gecko Unit',
}

const newUnitEntry = {
  id: '',
  type: 'unit',
  data: {},
  tags: {},
} as RolodexEntryPutJson

export const EditGeckoUnit = () => {
  const { siteID } = useParams({ strict: false })
  const { organization, forceRefresh, site } = useRightDrawerRolodexEntries({
    siteID,
  })
  const { drawerEntry, setDrawerEntry, setDrawerState, closeDrawer } =
    useRightDrawer()
  const { rolodexPut } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()
  const form = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: zodResolver(editUnitSchema),
  })

  const { setValue, handleSubmit, watch, control, trigger } = form
  const watchAllFields = watch()
  const unitHash = useMemo(() => uuid4().slice(0, 6), [])
  const {
    isGroupingInfoModalOpen,
    handleOpenGroupingInfoModal,
    handleCloseGroupingInfoModal,
    handleSaveGroupingInfo,
    handleCancelGroupingInfo,
    handleEditGroupingInfo,
    handlePatchToRolodex,
    groupingFormRef,
    groupingID,
    groupingInfoFormFields,
    existingGroupings,
  } = useGroupings(site, forceRefresh)

  const [isGroupingSelectionModalOpen, setIsGroupingSelectionModalOpen] =
    useState(false)
  const [selectedGroupingInfo, setSelectedGroupingInfo] =
    useState<GeckoSiteGrouping | null>(null)

  const onSelectGrouping = (groupingId: string) => {
    setValue('selectedGrouping', groupingId)
    const selectedGrouping = existingGroupings.find((g) => g.id === groupingId)
    setSelectedGroupingInfo(selectedGrouping || null)
    setIsGroupingSelectionModalOpen(false)
  }

  const updateSlug = (e: BaseSyntheticEvent) => {
    if (drawerEntry instanceof GeckoSite) {
      const entrySlug = generateEntrySlug(e.target.value, unitHash)
      setValue('name', e.target.value)
      setValue('unitSlug', entrySlug)
    }
  }

  useEffect(() => {
    if (drawerEntry) {
      if (drawerEntry instanceof GeckoUnit) {
        setValue('name', drawerEntry.data.name ?? 'Unknown Name')
        setValue('unitSlug', drawerEntry.id)
        setValue('description', drawerEntry.data.description ?? '')
        setValue('erpID', drawerEntry.data.erp_id ?? '')
        setValue('area', drawerEntry.data.area ?? '')
        setValue('areaDescription', drawerEntry.data.area_description ?? '')

        const unitGroupings = drawerEntry.data.groupings || []
        const selectedGroupingId =
          unitGroupings.length > 0 ? unitGroupings[0] : '' // first element of groupings is child, guaranteed by getGroupingHierarchy
        setValue('selectedGrouping', selectedGroupingId)

        if (selectedGroupingId) {
          const selectedGrouping = existingGroupings.find(
            (g) => g.id === selectedGroupingId
          )
          setSelectedGroupingInfo(selectedGrouping || null)
        } else {
          setSelectedGroupingInfo(null)
        }

        if (drawerEntry.data.built_date) {
          setValue('builtDate', new Date(drawerEntry.data.built_date))
        }
        if (drawerEntry.data.retirement_date) {
          setValue('retirementDate', new Date(drawerEntry.data.retirement_date))
        }
      }
    }
  }, [drawerEntry, setValue, existingGroupings])

  const containerTitle = drawerEntry instanceof GeckoUnit ? 'Editing' : 'Adding'
  const submitButtonText = drawerEntry instanceof GeckoUnit ? 'Save' : 'Create'

  const onSubmit = handleSubmit(async (data) => {
    const unitRawJSON: RolodexEntryPutJson =
      drawerEntry instanceof GeckoUnit ? drawerEntry.raw : newUnitEntry

    unitRawJSON.id = data.unitSlug
    unitRawJSON.data.name = data.name
    unitRawJSON.data.description = data.description
    unitRawJSON.data.erp_id = data.erpID
    unitRawJSON.data.area = data.area
    unitRawJSON.data.area_description = data.areaDescription
    unitRawJSON.data.retirement_date = data.retirementDate
      ? format(data.retirementDate, 'yyyy-MM-dd')
      : null
    unitRawJSON.data.built_date = data.builtDate
      ? format(data.builtDate, 'yyyy-MM-dd')
      : null
    // Save all groupings in hierarchy (selected + all parents)
    unitRawJSON.data.groupings = data.selectedGrouping
      ? getGroupingHierarchy(data.selectedGrouping, existingGroupings)
      : []
    unitRawJSON.tags.site = siteID ?? ''
    unitRawJSON.tags.organization = organization?.id ?? ''
    const action =
      drawerEntry instanceof GeckoUnit ? 'modified this' : 'created new'

    try {
      await rolodexPut([unitRawJSON])
      await forceRefresh()
      await messageApi.success(
        `Successfully ${drawerEntry instanceof GeckoUnit ? 'modified' : 'created'} asset!`,
        1
      )
      closeDrawer()
      setDrawerEntry(GeckoUnit.CreateFromJSON(unitRawJSON as RolodexEntryJson))
    } catch {
      messageApi.error(
        `Failed to ${action} asset! Please check permissions and try again.`
      )
    }
  })

  return (
    <FormProvider {...form}>
      <FormContainer>
        {contextHolder}
        <div>
          <StyledHeaderDiv>
            <h3>
              {containerTitle}: {watchAllFields.name}
            </h3>
            <h4>Slug: {watchAllFields.unitSlug}</h4>
          </StyledHeaderDiv>
          <StyledFormRoot onSubmit={onSubmit}>
            <Controller
              name='name'
              control={control}
              render={({
                field: { name, onBlur, onChange, disabled, ref },
                formState: { errors },
              }) => (
                <div>
                  <label htmlFor={name}>Name</label>
                  <StyledFormInput
                    data-testid='unitName'
                    autoComplete='off'
                    onBlur={onBlur}
                    disabled={disabled}
                    id={name}
                    defaultValue={
                      drawerEntry instanceof GeckoUnit
                        ? drawerEntry.data.name
                        : undefined
                    }
                    onChange={(e) => {
                      onChange(e)
                      updateSlug(e)
                    }}
                    ref={ref}
                    aria-invalid={!!errors.name}
                  />
                  {errors.name && (
                    <span role='alert'>{errors.name.message}</span>
                  )}
                </div>
              )}
            />

            <Controller
              name='description'
              control={control}
              render={({
                field: { name, onBlur, onChange, disabled, ref },
                formState: { errors },
              }) => (
                <div>
                  <label htmlFor={name}>Description</label>
                  <StyledFormInput
                    data-testid='unitDescription'
                    onBlur={onBlur}
                    disabled={disabled}
                    id={name}
                    defaultValue={
                      drawerEntry instanceof GeckoUnit
                        ? drawerEntry.data.description
                        : undefined
                    }
                    onChange={(e) => onChange(e.target.value)}
                    ref={ref}
                    aria-invalid={!!errors.description}
                  />
                  {errors.description && (
                    <span role='alert'>{errors.description.message}</span>
                  )}
                </div>
              )}
            />

            <Controller
              name='erpID'
              control={control}
              render={({
                field: { name, onBlur, onChange, disabled, ref },
                formState: { errors },
              }) => (
                <div>
                  <label htmlFor={name}>ERP ID</label>
                  <StyledFormInput
                    data-testid='erpID'
                    onBlur={onBlur}
                    disabled={disabled}
                    id={name}
                    defaultValue={
                      drawerEntry instanceof GeckoUnit
                        ? drawerEntry.data.erp_id
                        : undefined
                    }
                    onChange={(e) => onChange(e.target.value)}
                    ref={ref}
                    aria-invalid={!!errors.erpID}
                  />
                  {errors.erpID && (
                    <span role='alert'>{errors.erpID.message}</span>
                  )}
                </div>
              )}
            />

            <Controller
              name='area'
              control={control}
              render={({
                field: { name, onBlur, onChange, disabled, ref },
                formState: { errors },
              }) => (
                <div>
                  <label htmlFor={name}>Area</label>
                  <StyledFormInput
                    data-testid='area'
                    onBlur={onBlur}
                    disabled={disabled}
                    id={name}
                    defaultValue={
                      drawerEntry instanceof GeckoUnit
                        ? drawerEntry.data.area
                        : undefined
                    }
                    onChange={(e) => onChange(e.target.value)}
                    ref={ref}
                    aria-invalid={!!errors.area}
                  />
                  {errors.area && (
                    <span role='alert'>{errors.area.message}</span>
                  )}
                </div>
              )}
            />

            <Controller
              name='areaDescription'
              control={control}
              render={({
                field: { name, onBlur, onChange, disabled, ref },
                formState: { errors },
              }) => (
                <div>
                  <label htmlFor={name}>Area Description</label>
                  <StyledFormInput
                    data-testid='areaDescription'
                    onBlur={onBlur}
                    disabled={disabled}
                    id={name}
                    defaultValue={
                      drawerEntry instanceof GeckoUnit
                        ? drawerEntry.data.area_description
                        : undefined
                    }
                    onChange={(e) => onChange(e.target.value)}
                    ref={ref}
                    aria-invalid={!!errors.areaDescription}
                  />
                  {errors.areaDescription && (
                    <span role='alert'>{errors.areaDescription.message}</span>
                  )}
                </div>
              )}
            />

            <PermissionsGate
              requires={[
                PERMISSION_CANTILEVER_CORE_ADMIN,
                PERMISSION_CANTILEVER_CORE_EDIT,
                ...(siteID ? [getSiteEditorPermission(siteID)] : []),
              ]}>
              <div>
                <label>Grouping</label>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Button
                    type='default'
                    onClick={() => setIsGroupingSelectionModalOpen(true)}
                    style={{ flex: 1 }}>
                    {selectedGroupingInfo
                      ? 'Change Grouping'
                      : 'Select Grouping'}
                  </Button>
                  {selectedGroupingInfo && (
                    <Button
                      type='text'
                      danger
                      onClick={() => {
                        setValue('selectedGrouping', '')
                        setSelectedGroupingInfo(null)
                      }}>
                      Clear
                    </Button>
                  )}
                </div>
                {selectedGroupingInfo && (
                  <SelectedGroupingDisplay>
                    <GroupingName>{selectedGroupingInfo.name}</GroupingName>
                    {selectedGroupingInfo.description && (
                      <GroupingDescription>
                        {selectedGroupingInfo.description}
                      </GroupingDescription>
                    )}
                  </SelectedGroupingDisplay>
                )}
              </div>
            </PermissionsGate>

            <Controller
              name='builtDate'
              control={control}
              render={({
                field: { name, onBlur, onChange, disabled, ref },
                formState: { errors },
              }) => (
                <div>
                  <label htmlFor={name}>Built Date</label>
                  <CustomProvider theme='dark'>
                    <DatePicker
                      data-testid='builtDate'
                      ref={ref}
                      oneTap
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                      id={name}
                      name={name}
                      ranges={[]}
                      onBlur={onBlur}
                      onChange={(value) => {
                        onChange(value)
                        trigger('retirementDate')
                      }}
                      disabled={disabled}
                      format='yyyy-MM-dd'
                      placeholder='yyyy-MM-dd'
                      value={
                        watchAllFields.builtDate
                          ? new Date(watchAllFields.builtDate)
                          : null
                      }
                      aria-invalid={!!errors.builtDate}
                    />
                  </CustomProvider>
                  {errors.builtDate && (
                    <span role='alert'>{errors.builtDate.message}</span>
                  )}
                </div>
              )}
            />

            <Controller
              name='retirementDate'
              control={control}
              render={({
                field: { name, onBlur, onChange, disabled, ref },
                formState: { errors },
              }) => (
                <div>
                  <label htmlFor={name}>Retirement Date</label>
                  <CustomProvider theme='dark'>
                    <DatePicker
                      data-testid='retirementDate'
                      ref={ref}
                      oneTap
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                      id={name}
                      name={name}
                      ranges={[]}
                      onBlur={onBlur}
                      onChange={(value) => {
                        onChange(value)
                        trigger('builtDate')
                      }}
                      disabled={disabled}
                      format='yyyy-MM-dd'
                      placeholder='yyyy-MM-dd'
                      value={
                        watchAllFields.retirementDate
                          ? new Date(watchAllFields.retirementDate)
                          : null
                      }
                      aria-invalid={!!errors.retirementDate}
                    />
                  </CustomProvider>
                  {errors.retirementDate && (
                    <span role='alert'>{errors.retirementDate.message}</span>
                  )}
                </div>
              )}
            />

            <Tooltip
              title={
                !(drawerEntry instanceof GeckoUnit)
                  ? 'You must complete Unit creation before you can add components'
                  : undefined
              }>
              <Button
                onClick={() => setDrawerState(DrawerState.EditGeckoComponent)}
                disabled={!(drawerEntry instanceof GeckoUnit)}>
                Add or Edit Components
              </Button>
            </Tooltip>

            <StyledFormSubmitButton htmlType={'submit'}>
              {submitButtonText}
            </StyledFormSubmitButton>
          </StyledFormRoot>
        </div>
      </FormContainer>

      <GroupingsSelectionModal
        site={site}
        existingGroupings={existingGroupings}
        handleOpenGroupingInfoModal={handleOpenGroupingInfoModal}
        handleEditGroupingInfo={handleEditGroupingInfo}
        isGroupingInfoModalOpen={isGroupingInfoModalOpen}
        groupingFormRef={groupingFormRef}
        handleCloseGroupingInfoModal={handleCloseGroupingInfoModal}
        handleSaveGroupingInfo={handleSaveGroupingInfo}
        handlePatchToRolodex={handlePatchToRolodex}
        handleCancelGroupingInfo={handleCancelGroupingInfo}
        groupingID={groupingID}
        groupingInfoFormFields={groupingInfoFormFields}
        isOpen={isGroupingSelectionModalOpen}
        onCancel={() => setIsGroupingSelectionModalOpen(false)}
        selectedGroupingId={watchAllFields.selectedGrouping}
        onSelectGrouping={onSelectGrouping}
      />
    </FormProvider>
  )
}
